import {
  Avatar,
  Badge,
  Box,
  Card,
  Group,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  Activity,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CircleSlash,
  ClipboardCheck,
  ClipboardList,
  FlaskConical,
  Stethoscope,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
  XCircle,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "@/lib/api/client";
import { listExaminations } from "@/lib/api/examinations";
import { listPatients } from "@/lib/api/patients";
import { getWorklist } from "@/lib/api/examinations";
import { formatDateWithWeekday, formatMonthYear } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

interface DailyReport {
  date: string;
  examinations_total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_verdict: Record<string, number>;
}

const TYPE_COLORS: Record<string, string> = {
  initial: "brand",
  periodic: "violet",
  form_086: "indigo",
  form_083: "cyan",
  form_089: "orange",
};

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const canViewReports = useAuthStore((s) => s.hasPermission("report.read.daily"));
  const today = new Date().toISOString().slice(0, 10);

  const dailyQuery = useQuery({
    queryKey: ["dashboard-daily", today],
    queryFn: async () => {
      const { data } = await api.get<DailyReport>("/reports/daily", {
        params: { date: today },
      });
      return data;
    },
    enabled: canViewReports,
  });

  const patientsQuery = useQuery({
    queryKey: ["dashboard-patients"],
    queryFn: () => listPatients({ limit: 5 }),
  });

  const examsQuery = useQuery({
    queryKey: ["dashboard-exams"],
    queryFn: () => listExaminations({ limit: 6 }),
  });

  const worklistQuery = useQuery({
    queryKey: ["dashboard-worklist"],
    queryFn: () => getWorklist(true),
  });

  const daily = dailyQuery.data;
  const totalPatients = patientsQuery.data?.total ?? "—";
  const examsToday = daily?.examinations_total ?? "—";
  const pending = daily?.by_status?.pending_chief ?? 0;
  const completed = daily?.by_status?.completed ?? 0;
  const healthy = daily?.by_verdict?.healthy ?? 0;
  const restricted = daily?.by_verdict?.restricted_fit ?? 0;
  const unfit = daily?.by_verdict?.unfit_for_work ?? 0;

  const examTotal = daily?.examinations_total ?? 0;
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return t("dashboard.greeting.night");
    if (h < 12) return t("dashboard.greeting.morning");
    if (h < 18) return t("dashboard.greeting.day");
    return t("dashboard.greeting.evening");
  })();

  return (
    <Stack gap="xl">
      <Card
        padding="xl"
        radius="xl"
        style={{
          background:
            "linear-gradient(135deg, var(--mantine-color-brand-6) 0%, var(--mantine-color-brand-9) 100%)",
          color: "white",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap="xs" style={{ zIndex: 1 }}>
            <Text size="sm" fw={500} style={{ opacity: 0.85 }}>
              {greeting}
            </Text>
            <Title order={1} fw={800} lh={1.1} c="white">
              {user?.full_name ?? t("dashboard.title")}
            </Title>
            <Text size="md" style={{ opacity: 0.92 }}>
              {formatDateWithWeekday(new Date(), i18n.language)}
            </Text>
          </Stack>
          <ThemeIcon size={72} radius="xl" variant="white" color="brand">
            <Stethoscope size={36} strokeWidth={2} />
          </ThemeIcon>
        </Group>
      </Card>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          label={t("dashboard.stats.patients")}
          value={totalPatients}
          icon={<Users size={22} strokeWidth={2} />}
          color="brand"
          to="/patients"
        />
        <StatCard
          label={t("dashboard.stats.exams_today")}
          value={examsToday}
          icon={<Activity size={22} strokeWidth={2} />}
          color="brand"
          to="/examinations"
        />
        <StatCard
          label={t("dashboard.stats.exams_pending")}
          value={pending}
          icon={<ClipboardCheck size={22} strokeWidth={2} />}
          color="orange"
          to="/examinations"
        />
        <StatCard
          label={t("dashboard.stats.completed_today")}
          value={completed}
          icon={<CalendarDays size={22} strokeWidth={2} />}
          color="brand"
          to="/examinations"
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        {daily && examTotal > 0 && (
          <Card padding="xl" radius="xl">
            <Group gap="sm" mb="lg">
              <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                <Activity size={18} strokeWidth={2} />
              </ThemeIcon>
              <Title order={5} fw={700}>
                {t("dashboard.today_breakdown")}
              </Title>
            </Group>
            <Stack gap="md">
              <BreakdownRow
                icon={<CheckCircle2 size={18} strokeWidth={2} />}
                label={t("reports.fields.healthy")}
                value={healthy}
                total={examTotal}
                color="brand"
              />
              <BreakdownRow
                icon={<CircleSlash size={18} strokeWidth={2} />}
                label={t("reports.fields.restricted")}
                value={restricted}
                total={examTotal}
                color="orange"
              />
              <BreakdownRow
                icon={<XCircle size={18} strokeWidth={2} />}
                label={t("reports.fields.unfit")}
                value={unfit}
                total={examTotal}
                color="red"
              />
            </Stack>
          </Card>
        )}

        {daily && Object.keys(daily.by_type).length > 0 && (
          <Card padding="xl" radius="xl">
            <Group gap="sm" mb="lg">
              <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                <ClipboardList size={18} strokeWidth={2} />
              </ThemeIcon>
              <Title order={5} fw={700}>
                {t("dashboard.by_type")}
              </Title>
            </Group>
            <Stack gap="md">
              {Object.entries(daily.by_type).map(([type, count]) => (
                <BreakdownRow
                  key={type}
                  icon={<ClipboardCheck size={18} strokeWidth={2} />}
                  label={t(`examinations.types.${type}`, { defaultValue: type })}
                  value={count}
                  total={examTotal}
                  color={TYPE_COLORS[type] ?? "brand"}
                />
              ))}
            </Stack>
          </Card>
        )}


        {worklistQuery.data?.items && worklistQuery.data.items.length > 0 && (
          <Card padding="xl" radius="xl">
            <Group gap="sm" mb="lg" justify="space-between">
              <Group gap="sm">
                <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                  <FlaskConical size={18} strokeWidth={2} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Title order={5} fw={700}>
                    {t("dashboard.my_worklist")}
                  </Title>
                  <Text size="xs" c="dimmed">
                    {worklistQuery.data.items.length} {t("dashboard.items_open")}
                  </Text>
                </Stack>
              </Group>
              <UnstyledButton
                component={Link}
                to="/worklist"
                style={{
                  color: "var(--mantine-color-brand-7)",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <Group gap={4}>
                  {t("common.view_all")}
                  <ArrowRight size={14} strokeWidth={2.4} />
                </Group>
              </UnstyledButton>
            </Group>
            <Stack gap="xs">
              {worklistQuery.data.items.slice(0, 5).map((it) => (
                <UnstyledButton
                  key={it.section_id}
                  component={Link}
                  to={`/examinations/${it.examination_id}`}
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Stack gap={2}>
                      <Text size="sm" fw={600}>
                        {it.patient_name}
                      </Text>
                      <Group gap={6}>
                        <Badge size="xs" variant="light" color="gray" radius="sm">
                          {it.patient_card_number}
                        </Badge>
                        <Badge size="xs" variant="light" color="brand" radius="sm">
                          {t(`examinations.types.${it.examination_type}`, {
                            defaultValue: it.examination_type,
                          })}
                        </Badge>
                      </Group>
                    </Stack>
                    <ArrowRight size={16} strokeWidth={2} color="var(--mantine-color-gray-5)" />
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </Card>
        )}
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        <Card padding="xl" radius="xl">
          <Group gap="sm" mb="lg" justify="space-between">
            <Group gap="sm">
              <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                <UserPlus size={18} strokeWidth={2} />
              </ThemeIcon>
              <Title order={5} fw={700}>
                {t("dashboard.recent_patients")}
              </Title>
            </Group>
            <UnstyledButton
              component={Link}
              to="/patients"
              style={{
                color: "var(--mantine-color-brand-7)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Group gap={4}>
                {t("common.view_all")}
                <ArrowRight size={14} strokeWidth={2.4} />
              </Group>
            </UnstyledButton>
          </Group>
          {patientsQuery.data?.items.length ? (
            <Stack gap="xs">
              {patientsQuery.data.items.slice(0, 5).map((p) => (
                <UnstyledButton
                  key={p.id}
                  component={Link}
                  to={`/patients/${p.id}`}
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    background: "var(--mantine-color-gray-0)",
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <Avatar
                        size={36}
                        radius="md"
                        variant="gradient"
                        gradient={{ from: "brand.5", to: "brand.8", deg: 135 }}
                      >
                        <Text size="sm" fw={700} c="white">
                          {p.family_name?.[0]?.toUpperCase() ?? "—"}
                        </Text>
                      </Avatar>
                      <Stack gap={2}>
                        <Text size="sm" fw={600}>
                          {p.family_name} {p.given_name}
                        </Text>
                        <Group gap={6}>
                          <Text size="xs" c="dimmed" ff="monospace">
                            {p.card_number}
                          </Text>
                          <Text size="xs" c="dimmed">
                            · {t("patients.years_old", { age: p.age })}
                          </Text>
                        </Group>
                      </Stack>
                    </Group>
                    <ArrowRight size={16} strokeWidth={2} color="var(--mantine-color-gray-5)" />
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" size="sm" py="md" ta="center">
              {t("patients.empty")}
            </Text>
          )}
        </Card>

        <Card padding="xl" radius="xl">
          <Group gap="sm" mb="lg" justify="space-between">
            <Group gap="sm">
              <ThemeIcon size={36} radius="xl" variant="light" color="brand">
                <TrendingUp size={18} strokeWidth={2} />
              </ThemeIcon>
              <Title order={5} fw={700}>
                {t("dashboard.recent_examinations")}
              </Title>
            </Group>
            <UnstyledButton
              component={Link}
              to="/examinations"
              style={{
                color: "var(--mantine-color-brand-7)",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Group gap={4}>
                {t("common.view_all")}
                <ArrowRight size={14} strokeWidth={2.4} />
              </Group>
            </UnstyledButton>
          </Group>
          {examsQuery.data?.items.length ? (
            <Table verticalSpacing="sm">
              <Table.Tbody>
                {examsQuery.data.items.slice(0, 5).map((e) => (
                  <Table.Tr key={e.id}>
                    <Table.Td>
                      <Group gap={6}>
                        <Badge variant="light" color={TYPE_COLORS[e.type] ?? "brand"} radius="sm">
                          {t(`examinations.types.${e.type}`, { defaultValue: e.type })}
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={
                          e.status === "completed"
                            ? "brand"
                            : e.status === "pending_chief"
                              ? "orange"
                              : "gray"
                        }
                        size="sm"
                        radius="sm"
                      >
                        {t(`examinations.statuses.${e.status}`, { defaultValue: e.status })}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <UnstyledButton
                        component={Link}
                        to={`/examinations/${e.id}`}
                        style={{ color: "var(--mantine-color-brand-7)" }}
                      >
                        <ArrowRight size={16} strokeWidth={2} />
                      </UnstyledButton>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text c="dimmed" size="sm" py="md" ta="center">
              {t("examinations.empty")}
            </Text>
          )}
        </Card>
      </SimpleGrid>
    </Stack>
  );
}

function StatCard({
  label,
  value,
  icon,
  color,
  to,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  color: string;
  to?: string;
}) {
  const inner = (
    <Card padding="lg" radius="xl">
      <Group justify="space-between" align="flex-start" mb="md">
        <ThemeIcon size={48} radius="xl" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
      <Text c="dimmed" size="sm" fw={500}>
        {label}
      </Text>
      <Text size="2rem" fw={800} mt={2} lh={1}>
        {value}
      </Text>
    </Card>
  );
  if (to) {
    return (
      <Box
        component={Link}
        to={to}
        style={{ textDecoration: "none", color: "inherit", display: "block" }}
      >
        {inner}
      </Box>
    );
  }
  return inner;
}

function BreakdownRow({
  icon,
  label,
  value,
  total,
  color,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Stack gap={6}>
      <Group justify="space-between">
        <Group gap="sm">
          <ThemeIcon size={28} radius="xl" variant="light" color={color}>
            {icon}
          </ThemeIcon>
          <Text size="sm" fw={500}>
            {label}
          </Text>
        </Group>
        <Group gap="sm">
          <Text size="xs" c="dimmed" fw={600}>
            {pct}%
          </Text>
          <Text size="md" fw={800} c={`${color}.7`}>
            {value}
          </Text>
        </Group>
      </Group>
      <Progress value={pct} color={color} size="sm" radius="xl" />
    </Stack>
  );
}

function MiniStat({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  color: string;
}) {
  return (
    <Group gap="sm" p="sm" style={{ background: "var(--mantine-color-gray-0)", borderRadius: 12 }}>
      <ThemeIcon size={32} radius="xl" variant="light" color={color}>
        {icon}
      </ThemeIcon>
      <Stack gap={0}>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          {label}
        </Text>
        <Text size="md" fw={800}>
          {value}
        </Text>
      </Stack>
    </Group>
  );
}
